/**
 * A component's technical specification built as text (D-157) — Core's SpecificationText, for
 * the editor's pages: groups of features printed by their layout (list, inline, label),
 * punctuated as hand-made offers are; the equipment chosen in the offer in the group it names
 * (no name: the group marked for equipment); a fallback item only while no equipment fills its
 * group; a group with nothing to print left out. Returns TipTap nodes.
 */
export const LAYOUTS = ['list', 'inline', 'label']
const paragraph = (text) => ({ type: 'paragraph', content: [{ type: 'text', text }] })
const strip = (x) => x.replace(/[ ,;.]+$/, '')

export function specNodes(spec, slots = {}, lang = 'pl') {
  const t = (v) => (v && typeof v === 'object' ? String(v[lang] ?? v.pl ?? v.en ?? Object.values(v)[0] ?? '') : String(v ?? ''))
  const punctuate = spec?.punctuation !== false
  const groups = (spec?.groups || []).filter((g) => g && typeof g === 'object').map((g) => ({ ...g }))
  const keys = groups.map((g) => String(g.key || ''))
  const target = groups.find((g) => g.equipment)?.key ?? null
  const lines = {}
  for (const [slot, list] of Object.entries(slots || {})) {
    const key = slot !== '' && keys.includes(slot) ? slot : target ?? slot
    lines[key] = [...(lines[key] || []), ...(list || [])]
  }
  for (const slot of Object.keys(lines)) {
    if (!keys.includes(slot)) {
      groups.push({ key: slot, label: slot.charAt(0).toUpperCase() + slot.slice(1), layout: 'list', items: [] })
      keys.push(slot)
    }
  }
  const out = []
  for (const g of groups) {
    const label = t(g.label).trim()
    const own = []
    const fallback = []
    for (const item of g.items || []) {
      const text = t(item && typeof item === 'object' ? item.text : item).trim()
      if (text) (item?.fallback ? fallback : own).push(text)
    }
    const added = (lines[String(g.key || '')] || []).map((x) => String(x).trim()).filter(Boolean)
    const items = [...own, ...(added.length ? added : fallback)]
    const layout = LAYOUTS.includes(g.layout) ? g.layout : 'list'
    if (layout === 'label') {
      if (label) out.push(paragraph(label))
      continue
    }
    if (!items.length) continue
    if (layout === 'inline') {
      out.push(paragraph((label ? `${label}: ` : '') + items.map(strip).join(', ')))
      continue
    }
    if (label) out.push(paragraph(`${label.replace(/[ :]+$/, '')}:`))
    const last = items.length - 1
    out.push({ type: 'bulletList', content: items.map((x, i) => ({ type: 'listItem', content: [paragraph(punctuate ? strip(x) + (i === last ? '.' : ',') : x)] })) })
  }
  return out
}

/**
 * A specification read from a text written by hand (D-157), to start from: "Label:" followed by
 * a list is a list group, "Label: value" one inline, a paragraph alone a label; the items lose
 * their end punctuation (the builder puts it back). The first heading is left out (it stays the
 * section's own text).
 */
export function specFromDoc(doc, lang = 'pl') {
  const text = (n) => (n?.type === 'text' ? n.text || '' : (n?.content || []).map(text).join(''))
  const groups = []
  const keyOf = (label) => label.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ł/g, 'l').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 32) || `g${groups.length}`
  const nodes = doc?.content || []
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i]
    if (n.type === 'heading') continue
    const itemsOf = (list) => (list.content || []).map((li) => text(li).trim()).filter((x) => x && !/^\{\{.*\}\}$/.test(x)).map((x) => ({ text: { [lang]: strip(x) } }))
    // A list with no label before it: a group of its own, unlabelled.
    if (['bulletList', 'orderedList'].includes(n.type)) {
      groups.push({ key: `g${groups.length + 1}`, label: { [lang]: '' }, layout: 'list', items: itemsOf(n) })
      continue
    }
    if (n.type !== 'paragraph') continue
    const line = text(n).trim()
    if (!line || /^\{\{.*\}\}$/.test(line)) continue
    const next = nodes[i + 1]
    const list = next && ['bulletList', 'orderedList'].includes(next.type)
    const colon = line.indexOf(':')
    if (list) {
      const label = line.replace(/:\s*$/, '')
      groups.push({ key: keyOf(label), label: { [lang]: label }, layout: 'list', items: itemsOf(next) })
      i++
    } else if (colon > 0 && colon < line.length - 1) {
      const label = line.slice(0, colon).trim()
      groups.push({ key: keyOf(label), label: { [lang]: label }, layout: 'inline', items: line.slice(colon + 1).split(/,\s*/).map((x) => ({ text: { [lang]: strip(x.trim()) } })).filter((x) => x.text[lang]) })
    } else {
      groups.push({ key: keyOf(line), label: { [lang]: line.replace(/:\s*$/, '') }, layout: 'label', items: [] })
    }
  }
  return { groups, punctuation: true }
}
